/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/constants/analytics.ts
 * 版本: v1.0
 * 创建日期: 2025-12-13
 * 创建人: AI Assistant
 * 
 * 统计分析模块常量定义
 * 包含：色系、图标预设、卡片类型枚举
 */

import type { Component } from 'vue'
import {
  TrendCharts,
  List,
  Van,
  Warning,
  Monitor,
  Clock,
  CircleClose,
  User,
  Document,
  Coin,
  Money,
  Timer,
  DataLine,
  PieChart,
  Tickets,
  ShoppingCart,
  Connection
} from '@element-plus/icons-vue'
import { markRaw } from 'vue'

// ============================================================
// KPI 卡片类型枚举
// ============================================================

/**
 * KPI 卡片类型（对应不同的颜色主题）
 */
export type KpiCardType = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'secondary'

/**
 * 趋势方向
 */
export type TrendDirection = 'up' | 'down' | 'flat'

// ============================================================
// 色系定义
// ============================================================

/**
 * 统计分析统一色系
 * 
 * | 场景 | 颜色 | 用途 |
 * |------|------|------|
 * | primary | #1071E8 | 总量/主指标（品牌蓝） |
 * | success | #67C23A | 完成/活跃 |
 * | warning | #E6A23C | 待处理/预警 |
 * | danger | #F56C6C | 异常/拒绝 |
 * | info | #5470c6 | 次要指标（ECharts 默认靛蓝色，与趋势图主图一致） |
 * | secondary | #909399 | 其他/结算 |
 * 
 * v1.1 (2025-12-13): info 颜色从 #409EFF 调整为 #5470c6，与趋势图主图保持一致
 */
export const ANALYTICS_COLORS = {
  primary: '#1071E8',
  success: '#67C23A',
  warning: '#E6A23C',
  danger: '#F56C6C',
  info: '#5470c6',       // ⭐ v1.1: 调整为 ECharts 默认靛蓝色
  secondary: '#909399'
} as const

/**
 * 色系对应的 RGB 值（用于透明度渐变）
 */
export const ANALYTICS_COLORS_RGB = {
  primary: '16, 113, 232',
  success: '103, 194, 58',
  warning: '230, 162, 60',
  danger: '245, 108, 108',
  info: '84, 112, 198',   // ⭐ v1.1: 对应 #5470c6
  secondary: '144, 147, 153'
} as const

/**
 * 趋势图（L2）颜色配置
 * 
 * 与仪表盘时序图保持一致：
 * - 主图：ECharts 默认靛蓝色（#5470c6）
 * - 对比图（环比/同比）：danger 红色虚线
 * 
 * 注：仪表盘 TimeSeriesPanel 未指定主图颜色，使用 ECharts 默认调色板第一个颜色
 */
export const CHART_COLORS = {
  /** 主图颜色（柱状图/折线图/面积图）- ECharts 默认靛蓝色 */
  main: '#5470c6',
  /** 主图 RGBA（85% 透明度，用于柱状图） */
  mainRgba: 'rgba(84, 112, 198, 0.85)',
  /** 对比线颜色（环比/同比虚线） */
  compare: '#f56c6c',
  /** 对比线 RGBA */
  compareRgba: 'rgba(245, 108, 108, 0.8)'
} as const

/**
 * 获取颜色的 CSS 变量名
 */
export function getColorVar(type: KpiCardType): string {
  const varMap: Record<KpiCardType, string> = {
    primary: '--el-color-primary',
    success: '--el-color-success',
    warning: '--el-color-warning',
    danger: '--el-color-danger',
    info: '--el-color-info',
    secondary: '--el-color-info'
  }
  return varMap[type]
}

/**
 * 获取颜色值
 */
export function getColor(type: KpiCardType): string {
  return ANALYTICS_COLORS[type]
}

/**
 * 获取颜色 RGB 值
 */
export function getColorRgb(type: KpiCardType): string {
  return ANALYTICS_COLORS_RGB[type]
}

// ============================================================
// 图标预设
// ============================================================

/**
 * 统计分析模块图标映射（已使用 markRaw 包装）
 */
export const ANALYTICS_ICONS = {
  // GMV 相关
  gmv: markRaw(TrendCharts),
  money: markRaw(Money),
  coin: markRaw(Coin),
  
  // 订单相关
  order: markRaw(List),
  orders: markRaw(Tickets),
  cart: markRaw(ShoppingCart),
  
  // 配送相关
  delivery: markRaw(Van),
  clock: markRaw(Clock),
  timer: markRaw(Timer),
  
  // 工单/预警相关
  warning: markRaw(Warning),
  alert: markRaw(Warning),
  document: markRaw(Document),
  
  // 餐柜相关
  cabinet: markRaw(Monitor),
  device: markRaw(Monitor),
  
  // 用户相关
  user: markRaw(User),
  users: markRaw(User),
  
  // 其他
  error: markRaw(CircleClose),
  dataLine: markRaw(DataLine),
  pieChart: markRaw(PieChart),
  connection: markRaw(Connection)
} as const

/**
 * 根据名称获取图标组件
 * @param name 图标名称
 * @returns 图标组件（已 markRaw 包装）
 */
export function getAnalyticsIcon(name: keyof typeof ANALYTICS_ICONS): Component {
  return ANALYTICS_ICONS[name] || ANALYTICS_ICONS.dataLine
}

// ============================================================
// KPI 卡片预设配置
// ============================================================

/**
 * 预设的 KPI 卡片配置
 */
export interface KpiCardPreset {
  type: KpiCardType
  icon: Component
  label: string
}

/**
 * GMV 统计预设
 */
export const GMV_KPI_PRESETS: Record<string, KpiCardPreset> = {
  totalGmv: { type: 'primary', icon: ANALYTICS_ICONS.gmv, label: '总GMV' },
  todayGmv: { type: 'success', icon: ANALYTICS_ICONS.money, label: '今日GMV' },
  newGmv: { type: 'success', icon: ANALYTICS_ICONS.coin, label: '新增GMV' },
  lostGmv: { type: 'danger', icon: ANALYTICS_ICONS.warning, label: '流失GMV' }
}

/**
 * 订单统计预设
 */
export const ORDER_KPI_PRESETS: Record<string, KpiCardPreset> = {
  totalOrders: { type: 'primary', icon: ANALYTICS_ICONS.order, label: '总订单' },
  completedOrders: { type: 'success', icon: ANALYTICS_ICONS.orders, label: '完成订单' },
  cancelledOrders: { type: 'warning', icon: ANALYTICS_ICONS.warning, label: '取消订单' },
  refundedOrders: { type: 'danger', icon: ANALYTICS_ICONS.error, label: '退款订单' }
}

/**
 * 配送统计预设
 */
export const DELIVERY_KPI_PRESETS: Record<string, KpiCardPreset> = {
  totalDelivery: { type: 'primary', icon: ANALYTICS_ICONS.delivery, label: '总配送单' },
  pendingDelivery: { type: 'info', icon: ANALYTICS_ICONS.clock, label: '待接单/在途' },
  lateDelivery: { type: 'warning', icon: ANALYTICS_ICONS.warning, label: '迟到配送' },
  abnormalDelivery: { type: 'danger', icon: ANALYTICS_ICONS.error, label: '异常配送' }
}

/**
 * 工单预警预设
 */
export const ALERT_KPI_PRESETS: Record<string, KpiCardPreset> = {
  complaint: { type: 'danger', icon: ANALYTICS_ICONS.warning, label: '客诉工单' },
  mealSchedule: { type: 'warning', icon: ANALYTICS_ICONS.timer, label: '排餐预警' },
  recharge: { type: 'info', icon: ANALYTICS_ICONS.coin, label: '充值预警' },
  payment: { type: 'primary', icon: ANALYTICS_ICONS.money, label: '回款预警' },
  settlement: { type: 'secondary', icon: ANALYTICS_ICONS.document, label: '结算预警' },
  contract: { type: 'warning', icon: ANALYTICS_ICONS.document, label: '合同预警' }
}

/**
 * 餐柜统计预设
 */
export const CABINET_KPI_PRESETS: Record<string, KpiCardPreset> = {
  totalDevices: { type: 'primary', icon: ANALYTICS_ICONS.cabinet, label: '设备总数' },
  onlineDevices: { type: 'success', icon: ANALYTICS_ICONS.device, label: '在线设备' },
  abnormalDevices: { type: 'danger', icon: ANALYTICS_ICONS.error, label: '异常设备' },
  usageRate: { type: 'info', icon: ANALYTICS_ICONS.pieChart, label: '使用率' }
}

/**
 * 用户活跃度预设
 */
export const ACTIVITY_KPI_PRESETS: Record<string, KpiCardPreset> = {
  totalEmployees: { type: 'primary', icon: ANALYTICS_ICONS.user, label: '总员工' },
  activeDiners: { type: 'success', icon: ANALYTICS_ICONS.users, label: '活跃用餐' },
  diningRate: { type: 'info', icon: ANALYTICS_ICONS.pieChart, label: '平均用餐率' },
  monthlyOrders: { type: 'warning', icon: ANALYTICS_ICONS.orders, label: '本月订餐量' }
}

// ============================================================
// 趋势配置
// ============================================================

/**
 * 趋势文本配置
 */
export const TREND_CONFIG = {
  mom: { label: '环比', prefix: '' },
  yoy: { label: '同比', prefix: '' },
  rate: { label: '', suffix: '%' }
} as const

/**
 * 获取趋势方向
 */
export function getTrendDirection(value: number): TrendDirection {
  if (value > 0) return 'up'
  if (value < 0) return 'down'
  return 'flat'
}

/**
 * 格式化趋势文本
 * @param value 趋势值（百分比）
 * @param type 趋势类型
 */
export function formatTrendText(value: number, type: keyof typeof TREND_CONFIG = 'mom'): string {
  const config = TREND_CONFIG[type]
  const sign = value >= 0 ? '+' : ''
  return `${config.label} ${sign}${value.toFixed(1)}%`
}

// ============================================================
// 分类维度配置
// ============================================================

/**
 * 分类维度类型
 */
export type CategoryDimension = 
  // 通用维度
  | 'meal_segment'      // 餐段
  | 'service_type'      // 服务类型
  | 'status'            // 状态
  | 'new_lost'          // 新增/流失
  // GMV 维度
  | 'volume_tier'       // 流水分档
  | 'commission_type'   // 佣金类别
  // 订单维度
  | 'order_type'        // 订单类型
  | 'enterprise_type'   // 企业类型
  // 配送维度
  | 'incident'          // 异常事件
  // 餐柜维度
  | 'online_status'     // 在线状态
  | 'lighting'          // 灯光状态
  | 'temperature'       // 温度状态
  | 'door'              // 柜门状态
  // 工单预警维度（⭐ 新增）
  | 'complaint_severity'    // 客诉严重程度
  | 'complaint_category'    // 投诉分类
  | 'meal_schedule_status'  // 排餐状态
  | 'recharge_type'         // 充值类别
  | 'recharge_deadline'     // 充值到期统计
  | 'payment_period'        // 账期类别
  | 'payment_deadline'      // 回款到期统计
  | 'settlement_type'       // 结算类别
  | 'contract_status'       // 合同状态
  | 'contract_deadline'     // 合同到期统计

/**
 * 分类维度配置
 */
export interface CategoryDimensionConfig {
  key: CategoryDimension
  label: string
  module: 'gmv' | 'orders' | 'delivery' | 'alerts' | 'cabinet' | 'activity'
}

/**
 * GMV 分类维度
 */
export const GMV_CATEGORY_DIMENSIONS: CategoryDimensionConfig[] = [
  { key: 'meal_segment', label: '餐段', module: 'gmv' },
  { key: 'service_type', label: '服务类型', module: 'gmv' },
  { key: 'volume_tier', label: '流水分档', module: 'gmv' },
  { key: 'commission_type', label: '佣金类别', module: 'gmv' },
  { key: 'new_lost', label: '新增/流失', module: 'gmv' }
]

/**
 * 订单分类维度
 */
export const ORDER_CATEGORY_DIMENSIONS: CategoryDimensionConfig[] = [
  { key: 'status', label: '订单状态', module: 'orders' },
  { key: 'meal_segment', label: '餐段', module: 'orders' },
  { key: 'order_type', label: '订单类型', module: 'orders' },
  { key: 'enterprise_type', label: '企业类型', module: 'orders' },
  { key: 'new_lost', label: '新增/流失', module: 'orders' }
]

/**
 * 配送分类维度
 */
export const DELIVERY_CATEGORY_DIMENSIONS: CategoryDimensionConfig[] = [
  { key: 'meal_segment', label: '餐段', module: 'delivery' },
  { key: 'status', label: '配送状态', module: 'delivery' },
  { key: 'incident', label: '异常事件', module: 'delivery' }
]

/**
 * 餐柜分类维度
 */
export const CABINET_CATEGORY_DIMENSIONS: CategoryDimensionConfig[] = [
  { key: 'online_status', label: '在线状态', module: 'cabinet' },
  { key: 'lighting', label: '灯光状态', module: 'cabinet' },
  { key: 'temperature', label: '温度状态', module: 'cabinet' },
  { key: 'door', label: '柜门状态', module: 'cabinet' }
]

// ============================================================
// 工单预警分类维度（⭐ M8.1 新增）
// ============================================================

/**
 * 工单预警分类维度配置接口
 */
export interface AlertCategoryDimensionConfig {
  key: CategoryDimension
  label: string
  alertType: 'complaint' | 'meal_schedule' | 'recharge' | 'payment' | 'settlement' | 'contract'
}

/**
 * 客诉工单分类维度
 * - 投诉工单：严重客诉、一般客诉、轻微客诉
 * - 事故工单（可选扩展）
 */
export const COMPLAINT_CATEGORY_DIMENSIONS: AlertCategoryDimensionConfig[] = [
  { key: 'complaint_severity', label: '客诉严重程度', alertType: 'complaint' },
  { key: 'complaint_category', label: '投诉分类', alertType: 'complaint' }
]

/**
 * 排餐预警分类维度
 * - 全餐段、临近N小时未排、到期未排
 */
export const MEAL_SCHEDULE_CATEGORY_DIMENSIONS: AlertCategoryDimensionConfig[] = [
  { key: 'meal_segment', label: '餐段', alertType: 'meal_schedule' },
  { key: 'meal_schedule_status', label: '排餐状态', alertType: 'meal_schedule' }
]

/**
 * 充值预警分类维度
 * - 充值类别：无充值、日充值、周充值、月充值、年充值
 * - 到期统计：临近1h未充、临近1h未充、超期未充
 */
export const RECHARGE_CATEGORY_DIMENSIONS: AlertCategoryDimensionConfig[] = [
  { key: 'recharge_type', label: '充值类别', alertType: 'recharge' },
  { key: 'recharge_deadline', label: '到期统计', alertType: 'recharge' }
]

/**
 * 回款预警分类维度
 * - 账期类别：60天周期、75天周期、90天周期、120天周期
 * - 到期统计：到期前3天、到期前2天、到期前1天
 */
export const PAYMENT_CATEGORY_DIMENSIONS: AlertCategoryDimensionConfig[] = [
  { key: 'payment_period', label: '账期类别', alertType: 'payment' },
  { key: 'payment_deadline', label: '到期统计', alertType: 'payment' }
]

/**
 * 结算预警分类维度
 * - 结算类别
 */
export const SETTLEMENT_CATEGORY_DIMENSIONS: AlertCategoryDimensionConfig[] = [
  { key: 'settlement_type', label: '结算类别', alertType: 'settlement' }
]

/**
 * 合同预警分类维度
 * - 合同状态
 * - 有效期到期分析
 */
export const CONTRACT_CATEGORY_DIMENSIONS: AlertCategoryDimensionConfig[] = [
  { key: 'contract_status', label: '合同状态', alertType: 'contract' },
  { key: 'contract_deadline', label: '到期统计', alertType: 'contract' }
]

/**
 * 所有工单预警分类维度（汇总）
 */
export const ALL_ALERT_CATEGORY_DIMENSIONS: AlertCategoryDimensionConfig[] = [
  ...COMPLAINT_CATEGORY_DIMENSIONS,
  ...MEAL_SCHEDULE_CATEGORY_DIMENSIONS,
  ...RECHARGE_CATEGORY_DIMENSIONS,
  ...PAYMENT_CATEGORY_DIMENSIONS,
  ...SETTLEMENT_CATEGORY_DIMENSIONS,
  ...CONTRACT_CATEGORY_DIMENSIONS
]

// ============================================================
// 工单预警分类选项（⭐ M8.1 新增）
// ============================================================

/**
 * 客诉严重程度选项
 */
export const COMPLAINT_SEVERITY_OPTIONS = [
  { code: 'severe', label: '严重客诉', color: ANALYTICS_COLORS.danger },
  { code: 'normal', label: '一般客诉', color: ANALYTICS_COLORS.warning },
  { code: 'minor', label: '轻微客诉', color: ANALYTICS_COLORS.info }
] as const

/**
 * 排餐状态选项
 */
export const MEAL_SCHEDULE_STATUS_OPTIONS = [
  { code: 'near_4h', label: '临近4小时', color: ANALYTICS_COLORS.danger },
  { code: 'near_1h', label: '临近1小时', color: ANALYTICS_COLORS.warning },
  { code: 'expired', label: '到期未排', color: ANALYTICS_COLORS.secondary }
] as const

/**
 * 充值类别选项
 */
export const RECHARGE_TYPE_OPTIONS = [
  { code: 'none', label: '无充值', color: ANALYTICS_COLORS.secondary },
  { code: 'daily', label: '日充值', color: ANALYTICS_COLORS.info },
  { code: 'weekly', label: '周充值', color: ANALYTICS_COLORS.success },
  { code: 'monthly', label: '月充值', color: ANALYTICS_COLORS.primary },
  { code: 'yearly', label: '年充值', color: ANALYTICS_COLORS.warning }
] as const

/**
 * 充值到期统计选项
 */
export const RECHARGE_DEADLINE_OPTIONS = [
  { code: 'near_1h', label: '临近1h未充', color: ANALYTICS_COLORS.warning },
  { code: 'near_1d', label: '临近1d未充', color: ANALYTICS_COLORS.warning },
  { code: 'expired', label: '超期未充', color: ANALYTICS_COLORS.danger }
] as const

/**
 * 账期类别选项
 */
export const PAYMENT_PERIOD_OPTIONS = [
  { code: '60d', label: '60天周期', color: ANALYTICS_COLORS.primary },
  { code: '75d', label: '75天周期', color: ANALYTICS_COLORS.info },
  { code: '90d', label: '90天周期', color: ANALYTICS_COLORS.warning },
  { code: '120d', label: '120天周期', color: ANALYTICS_COLORS.danger }
] as const

/**
 * 回款到期统计选项
 */
export const PAYMENT_DEADLINE_OPTIONS = [
  { code: 'before_3d', label: '到期前3天', color: ANALYTICS_COLORS.info },
  { code: 'before_2d', label: '到期前2天', color: ANALYTICS_COLORS.warning },
  { code: 'before_1d', label: '到期前1天', color: ANALYTICS_COLORS.danger }
] as const

/**
 * 合同状态选项
 */
export const CONTRACT_STATUS_OPTIONS = [
  { code: 'active', label: '有效', color: ANALYTICS_COLORS.success },
  { code: 'expiring', label: '即将到期', color: ANALYTICS_COLORS.warning },
  { code: 'expired', label: '已到期', color: ANALYTICS_COLORS.danger }
] as const

/**
 * 合同到期统计选项
 */
export const CONTRACT_DEADLINE_OPTIONS = [
  { code: 'in_30d', label: '30天内到期', color: ANALYTICS_COLORS.warning },
  { code: 'in_7d', label: '7天内到期', color: ANALYTICS_COLORS.danger },
  { code: 'expired', label: '已到期', color: ANALYTICS_COLORS.secondary }
] as const

// ============================================================
// 视角配置
// ============================================================

/**
 * 排行视角类型
 */
export type RankingViewType = 'enterprise' | 'merchant' | 'courier' | 'type'

/**
 * 视角配置
 */
export interface RankingViewConfig {
  key: RankingViewType
  label: string
}

/**
 * 通用排行视角
 */
export const COMMON_RANKING_VIEWS: RankingViewConfig[] = [
  { key: 'enterprise', label: '企业视角' },
  { key: 'merchant', label: '商户视角' }
]

/**
 * 配送排行视角
 */
export const DELIVERY_RANKING_VIEWS: RankingViewConfig[] = [
  { key: 'merchant', label: '商户排行' },
  { key: 'courier', label: '配送员排行' }
]

/**
 * 客诉排行视角
 */
export const COMPLAINT_RANKING_VIEWS: RankingViewConfig[] = [
  { key: 'merchant', label: '商户排行' },
  { key: 'enterprise', label: '企业排行' },
  { key: 'type', label: '类型排行' }
]

// ============================================================
// 客诉专用色系（M8.2 新增）
// ============================================================

/**
 * 客诉分析专用色系（4个一级分类）
 * 
 * 这些颜色用于客诉分析的饼图和分类区分，
 * 与 KPI 卡片色系独立，避免语义混淆。
 * 
 * @since v1.1 (2025-12-13)
 */
export const COMPLAINT_CATEGORY_COLORS = {
  MEAL: '#4fc3f7',       // 餐品类 - 亮蓝
  DELIVERY: '#81c784',   // 配送类 - 绿色
  SERVICE: '#f6c34c',    // 服务类 - 橙黄
  SYSTEM: '#ba68c8'      // 系统类 - 紫色
} as const

/**
 * 客诉一级分类颜色映射数组（用于饼图）
 */
export const COMPLAINT_PIE_COLORS = [
  COMPLAINT_CATEGORY_COLORS.MEAL,
  COMPLAINT_CATEGORY_COLORS.DELIVERY,
  COMPLAINT_CATEGORY_COLORS.SERVICE,
  COMPLAINT_CATEGORY_COLORS.SYSTEM
] as const

/**
 * 获取客诉一级分类颜色
 * @param code 一级分类 code (MEAL/DELIVERY/SERVICE/SYSTEM)
 * @returns 对应颜色，未匹配返回 secondary 色
 */
export function getComplaintCategoryColor(code: string): string {
  return COMPLAINT_CATEGORY_COLORS[code as keyof typeof COMPLAINT_CATEGORY_COLORS] || ANALYTICS_COLORS.secondary
}

